/**
 * Direct-iframe Hyperframes player.
 *
 * Renders a plain `<iframe srcDoc={html}>` and drives the GSAP timeline that
 * lives at `iframe.contentWindow.__timelines[compositionId]` from a single
 * wall-clock rAF loop. Audio elements with `data-start` / `data-duration`
 * are scheduled in lock-step with the timeline so SFX & music play at the
 * correct positions and respect mute / seek / pause.
 *
 * All mutable runtime state is held in refs (not React state). React state
 * is used only to trigger a re-render when the iframe finishes loading
 * (so we can size correctly).
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type HfPlayerListener = (e?: unknown) => void;
export type HfEventKind = "play" | "pause" | "ended" | "frameupdate" | "ratechange";

export type HfPlayerRef = {
  play(): void;
  pause(): void;
  seekTo(frame: number): void;
  mute(): void;
  unmute(): void;
  isFullscreen(): boolean;
  requestFullscreen(): void;
  exitFullscreen(): void;
  getCurrentFrame(): number;
  isPlaying(): boolean;
  setPlaybackRate(rate: number): void;
  addEventListener(event: HfEventKind, fn: HfPlayerListener): void;
  removeEventListener(event: HfEventKind, fn: HfPlayerListener): void;
};

type Props = {
  srcDoc?: string;
  src?: string;
  compositionWidth: number;
  compositionHeight: number;
  fps: number;
  loop?: boolean;
  autoPlay?: boolean;
  initialMuted?: boolean;
  /**
   * Selektives Mute pro Audio-Kind via `data-audio-kind="sfx|music|voiceover"`.
   * Toggeln dieser Props erfolgt zur LAUFZEIT — kein Composition-Rebuild,
   * kein iframe-Reload, keine Bildstotterer. Die Composition-HTML wird
   * IMMER mit allen Audio-Spuren gebaut; das Stummschalten passiert nur
   * in `tick()` durch Setzen von `el.muted`.
   */
  sfxMuted?: boolean;
  musicMuted?: boolean;
  voiceoverMuted?: boolean;
  /**
   * Per-Kind Volume-Multiplier (0..1). Skaliert oben auf die in der
   * Composition gebaute `data-volume` und greift gemeinsam mit der per-
   * Element berechneten Fade-In/Out-Kurve. Auch das ist ein reines
   * Runtime-Property — kein Rebuild beim Verschieben des Sliders.
   */
  sfxVolume?: number;
  musicVolume?: number;
  voiceoverVolume?: number;
  controls?: boolean;
  displayWidth: number;
  displayHeight: number;
  style?: React.CSSProperties;
  className?: string;
  remountKey?: number;
  onReady?: (player: HfPlayerRef | null) => void;
  /**
   * Authoritative reel total length, in frames. When provided, the runtime
   * uses this as the playback boundary INSTEAD of the GSAP timeline's
   * intrinsic `tl.duration()`. Required for two reasons:
   *
   *   (1) `tl.duration()` is implicit — it is computed from the latest tween
   *       end position. Scene builders that schedule animations past their
   *       `clip.durSec` (e.g. a long ease-out) inflate it beyond what the
   *       Composer/timeline UI shows as total. The transport-bar counter
   *       then runs PAST `totalFrames`.
   *   (2) `calcReelDurationSec` (the composer's calc) considers music
   *       `durSec`, overlay `fromSec+durSec` and SFX events. If music ends
   *       LATER than the last visible scene, GSAP's intrinsic duration is
   *       too SHORT and the counter would freeze early.
   *
   * Either case → mismatch between counter and player. Passing the
   * composer-side total here keeps both in lock-step.
   */
  durationInFrames?: number;
};

type GsapTimeline = {
  duration: () => number;
  time: (() => number) & ((t: number, suppressEvents?: boolean) => GsapTimeline);
  seek: (t: number, suppressEvents?: boolean) => GsapTimeline;
  play: () => GsapTimeline;
  pause: () => GsapTimeline;
};

type TimedMedia = {
  el: HTMLAudioElement | HTMLVideoElement;
  start: number;
  duration: number;
  /** Aus `data-volume` gelesen — die Build-Zeit-Volume des Clips. Wird zur
   *  Laufzeit mit dem Per-Kind-Volume und der Fade-Kurve multipliziert. */
  baseVolume: number;
  /**
   * Wo IM QUELL-FILE die Wiedergabe startet (Sekunden) — entspricht dem
   * `data-media-start`-Attribut. Default 0 (Datei vom Anfang). Für ein
   * Voiceover, das ab Sekunde 5 der Aufnahme abgespielt werden soll, ist
   * `mediaStart = 5`. Die effektive `currentTime` des Audios berechnet
   * sich als `mediaStart + (timelineT - start)`.
   */
  mediaStart: number;
  /** Aus `data-fade-in`/`data-fade-out` gelesen (Sekunden). Player rechnet
   *  damit pro Frame den Multiplier selbst aus, ohne von GSAP-Volume-
   *  Tweens abhängig zu sein. */
  fadeInSec: number;
  fadeOutSec: number;
  /** "sfx" | "music" | "voiceover" | undefined — steuert das selektive Mute + Volume. */
  kind: "sfx" | "music" | "voiceover" | undefined;
};

export const HyperframesPlayer = forwardRef<HfPlayerRef, Props>(function HyperframesPlayer(
  {
    srcDoc,
    src,
    compositionWidth,
    compositionHeight,
    fps,
    loop = false,
    autoPlay = false,
    initialMuted = false,
    sfxMuted = false,
    musicMuted = false,
    voiceoverMuted = false,
    sfxVolume = 1,
    musicVolume = 1,
    voiceoverVolume = 1,
    displayWidth,
    displayHeight,
    style,
    className,
    remountKey = 0,
    onReady,
    durationInFrames,
  },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Wenn der User in den Vollbild-Modus geht, wollen wir NICHT das iframe
  // selbst fullscreen schalten (das behält seine inline width/height von
  // 1080×1920 und sieht oben-links festgeklebt aus). Stattdessen schalten
  // wir den Wrapper fullscreen und re-skalieren das iframe auf die echte
  // Viewport-Größe. Diese State-Variable triggert nur Re-Renderings für
  // die Layout-Berechnung — alle Playback-State bleibt in `stateRef`.
  const [fsSize, setFsSize] = useState<{ w: number; h: number } | null>(null);

  // All player runtime state in refs — survives re-renders without
  // re-binding rAF callbacks.
  const stateRef = useRef({
    tl: null as GsapTimeline | null,
    duration: 0,
    paused: true,
    muted: initialMuted,
    /** Selektives Mute pro Kind — wird live von Props getriggert. */
    sfxMuted,
    musicMuted,
    voiceoverMuted,
    /** Per-Kind Volume-Multiplier (0..1). */
    sfxVolume,
    musicVolume,
    voiceoverVolume,
    rate: 1,
    loop,
    fps,
    media: [] as TimedMedia[],
    playStartTime: 0,
    playStartClock: 0,
    raf: 0,
    listeners: new Map<HfEventKind, Set<HfPlayerListener>>(),
  });

  // Wenn `srcDoc` sich ändert, lädt das iframe neu und die GSAP-Timeline ist
  // weg. Wir merken uns die letzte Player-Zeit (und ob er gerade lief), damit
  // wir nach dem Reload an exakt derselben Stelle weitermachen können — sonst
  // springt der Playhead bei jedem Edit (Inspector, Drag-Commit etc.) zurück
  // an den Anfang der Composition.
  const restoreRef = useRef<{ time: number; wasPlaying: boolean } | null>(null);

  // Keep refs in sync with props on every render.
  stateRef.current.loop = loop;
  stateRef.current.fps = fps;

  const emit = (event: HfEventKind, detail?: unknown): void => {
    stateRef.current.listeners.get(event)?.forEach((cb) => cb(detail));
  };

  // Single, never-recreated rAF tick — captures `stateRef` only.
  const tick = (): void => {
    const s = stateRef.current;
    if (!s.tl || s.paused) {
      s.raf = 0;
      return;
    }
    const now = performance.now();
    const elapsed = ((now - s.playStartClock) / 1000) * s.rate;
    let t = s.playStartTime + elapsed;
    if (s.duration > 0 && t >= s.duration) {
      if (s.loop) {
        s.playStartTime = 0;
        s.playStartClock = now;
        t = 0;
      } else {
        t = s.duration;
        try { s.tl.seek(t, false); } catch { /* ignore */ }
        s.paused = true;
        for (const m of s.media) m.el.pause();
        emit("pause");
        emit("ended");
        s.raf = 0;
        return;
      }
    }
    try { s.tl.seek(t, false); } catch { /* ignore */ }
    for (const m of s.media) {
      const local = t - m.start;
      if (local >= 0 && local < m.duration) {
        // Effektive Quell-Position: das Audio startet NICHT zwingend am
        // Anfang des Files. `mediaStart` (data-media-start) gibt den
        // Schnittpunkt an — z. B. springt ein Voiceover-Trim auf 5s im
        // Original. Wir clampen anschließend an die echte Datei-Dauer,
        // damit kurze Files nicht überschritten werden.
        const desired = m.mediaStart + local;
        const fileDur = m.el.duration;
        const safeDesired = Number.isFinite(fileDur) && fileDur > 0
          ? Math.max(0, Math.min(desired, fileDur))
          : Math.max(0, desired);
        if (m.el.paused) {
          try {
            m.el.currentTime = safeDesired;
            void m.el.play().catch(() => undefined);
          } catch { /* ignore */ }
        } else if (Math.abs(m.el.currentTime - safeDesired) > 0.15) {
          try { m.el.currentTime = safeDesired; } catch { /* ignore */ }
        }
        // Master-Mute + per-Kind-Mute: alles, was abgehakt ist, sofort still.
        const kindMuted =
          (m.kind === "sfx" && s.sfxMuted) ||
          (m.kind === "music" && s.musicMuted) ||
          (m.kind === "voiceover" && s.voiceoverMuted);
        const shouldMute = s.muted || kindMuted;
        if (m.el.muted !== shouldMute) m.el.muted = shouldMute;

        // Per-Kind Volume-Multiplier — vom UI-Slider kommend, ohne Rebuild.
        const kindVol =
          m.kind === "sfx" ? s.sfxVolume :
          m.kind === "music" ? s.musicVolume :
          m.kind === "voiceover" ? s.voiceoverVolume :
          1;

        // Fade-Multiplier nativ ausrechnen (vermeidet Konflikt mit GSAP-
        // Volume-Tween, der jede Frame von tick() überschrieben wurde).
        // Beide Fades werden linear skaliert; Edges weich.
        let fadeMul = 1;
        if (m.fadeInSec > 0 && local < m.fadeInSec) {
          fadeMul = Math.max(0, Math.min(1, local / m.fadeInSec));
        }
        if (m.fadeOutSec > 0) {
          const fadeOutStart = m.duration - m.fadeOutSec;
          if (local > fadeOutStart) {
            const f = Math.max(0, Math.min(1, (m.duration - local) / m.fadeOutSec));
            fadeMul = Math.min(fadeMul, f);
          }
        }
        const targetVol = Math.max(0, Math.min(1, m.baseVolume * kindVol * fadeMul));
        if (Math.abs(m.el.volume - targetVol) > 0.001) m.el.volume = targetVol;
      } else if (!m.el.paused) {
        m.el.pause();
      }
    }
    const cbs = s.listeners.get("frameupdate");
    if (cbs && cbs.size > 0) {
      const frame = Math.round(t * s.fps);
      cbs.forEach((cb) => cb({ detail: { frame } }));
    }
    s.raf = requestAnimationFrame(tick);
  };

  const doPlay = (): void => {
    const s = stateRef.current;
    if (!s.tl) return;
    const currentT = s.tl.time();
    let resumeFrom = currentT;
    if (s.duration > 0 && currentT >= s.duration - 0.01) resumeFrom = 0;
    s.playStartTime = resumeFrom;
    s.playStartClock = performance.now();
    s.paused = false;
    emit("play");
    if (!s.raf) s.raf = requestAnimationFrame(tick);
  };

  const doPause = (): void => {
    const s = stateRef.current;
    s.paused = true;
    if (s.raf) {
      cancelAnimationFrame(s.raf);
      s.raf = 0;
    }
    for (const m of s.media) m.el.pause();
    emit("pause");
  };

  const doSeek = (t: number): void => {
    const s = stateRef.current;
    if (!s.tl) return;
    const clamped = Math.max(0, Math.min(t, s.duration || t));
    try { s.tl.seek(clamped, false); } catch { /* ignore */ }
    s.playStartTime = clamped;
    s.playStartClock = performance.now();
    for (const m of s.media) {
      const local = clamped - m.start;
      if (local >= 0 && local < m.duration) {
        const desired = m.mediaStart + local;
        const fileDur = m.el.duration;
        const safeDesired = Number.isFinite(fileDur) && fileDur > 0
          ? Math.max(0, Math.min(desired, fileDur))
          : Math.max(0, desired);
        try { m.el.currentTime = safeDesired; } catch { /* ignore */ }
      } else {
        m.el.pause();
      }
    }
    const cbs = s.listeners.get("frameupdate");
    if (cbs && cbs.size > 0) {
      const frame = Math.round(clamped * s.fps);
      cbs.forEach((cb) => cb({ detail: { frame } }));
    }
  };

  const makeApi = (): HfPlayerRef => {
    const s = stateRef.current;
    return {
      play: doPlay,
      pause: doPause,
      seekTo: (frame: number) => doSeek(frame / s.fps),
      mute: () => {
        s.muted = true;
        for (const m of s.media) m.el.muted = true;
      },
      unmute: () => {
        s.muted = false;
        for (const m of s.media) m.el.muted = false;
      },
      isFullscreen: () => Boolean(document.fullscreenElement && document.fullscreenElement === wrapperRef.current),
      requestFullscreen: () => wrapperRef.current?.requestFullscreen?.(),
      exitFullscreen: () => { if (document.fullscreenElement) document.exitFullscreen(); },
      getCurrentFrame: () => (s.tl ? Math.round(s.tl.time() * s.fps) : 0),
      isPlaying: () => !s.paused,
      setPlaybackRate: (rate: number) => {
        const currentT = s.tl ? s.tl.time() : 0;
        s.rate = rate;
        s.playStartTime = currentT;
        s.playStartClock = performance.now();
        for (const m of s.media) m.el.playbackRate = rate;
      },
      addEventListener: (event, fn) => {
        let set = s.listeners.get(event);
        if (!set) { set = new Set(); s.listeners.set(event, set); }
        set.add(fn);
      },
      removeEventListener: (event, fn) => s.listeners.get(event)?.delete(fn),
    };
  };

  useImperativeHandle(ref, makeApi);

  // Wire iframe → timeline + media after each load (covers initial mount AND
  // srcDoc/src updates which both trigger a fresh navigation).
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const wire = (): void => {
      const s = stateRef.current;
      // Cancel any in-flight rAF from the previous iframe.
      if (s.raf) cancelAnimationFrame(s.raf);
      s.raf = 0;
      s.tl = null;
      s.media = [];
      s.paused = true;
      s.playStartTime = 0;

      const win = iframe.contentWindow as
        | (Window & { __timelines?: Record<string, GsapTimeline>; __player?: unknown })
        | null;
      if (!win) return;

      // Mark iframe as externally-driven for any in-HTML failsafe checks.
      try {
        win.__player = {
          getDuration: () => stateRef.current.duration,
          seek: (t: number) => doSeek(t),
          play: () => doPlay(),
          pause: () => doPause(),
        };
      } catch { /* ignore */ }

      const start = performance.now();
      const maxWaitMs = 5000;
      const poll = (): void => {
        const timelines = win.__timelines;
        if (timelines) {
          const keys = Object.keys(timelines);
          if (keys.length > 0) {
            const doc = iframe.contentDocument;
            const preferredId = doc?.querySelector("[data-composition-id]")?.getAttribute("data-composition-id");
            const id = preferredId && preferredId in timelines ? preferredId : keys[keys.length - 1];
            const tl = timelines[id];
            if (tl && typeof tl.duration === "function" && typeof tl.seek === "function") {
              const tlDur = tl.duration() || 0;
              // Prefer the composer-computed total (frames → seconds) as the
              // playback boundary. Falls back to GSAP's intrinsic duration
              // when the prop is missing (e.g. standalone use of this player
              // outside of ReelPlayerPreview). See the prop docs for why.
              const propDurSec =
                typeof durationInFrames === "number" && durationInFrames > 0
                  ? durationInFrames / stateRef.current.fps
                  : 0;
              const dur = propDurSec > 0 ? propDurSec : tlDur;
              stateRef.current.tl = tl;
              stateRef.current.duration = dur;
              // Wenn dieser Reload durch eine srcDoc-Änderung ausgelöst wurde
              // (Edit im Composer), wollen wir die vorherige Playhead-Position
              // wiederherstellen, statt zurück auf 0 zu springen. Bei einem
              // Hard-Remount (remountKey) ist `restoreRef` nicht gesetzt und
              // wir starten regulär bei 0.
              const restore = restoreRef.current;
              restoreRef.current = null;
              const initialT = restore ? Math.max(0, Math.min(restore.time, dur)) : 0;
              try { tl.seek(initialT, false); } catch { /* ignore */ }
              stateRef.current.playStartTime = initialT;
              // Adopt timed media.
              if (doc) {
                const nodes = doc.querySelectorAll<HTMLAudioElement | HTMLVideoElement>(
                  "audio[data-start], video[data-start]",
                );
                const collected: TimedMedia[] = [];
                nodes.forEach((el) => {
                  const tStart = parseFloat(el.getAttribute("data-start") || "0");
                  const tDur = parseFloat(el.getAttribute("data-duration") || "Infinity");
                  const vol = parseFloat(el.getAttribute("data-volume") || "1");
                  // `data-media-start` ist der Schnitt-Offset im Quell-File
                  // (Sekunden). Wenn das Voiceover ab Sekunde 5 abgespielt
                  // werden soll, ist das hier 5. Defaults to 0.
                  const mediaStartRaw = parseFloat(el.getAttribute("data-media-start") || "0");
                  const mediaStart = Number.isFinite(mediaStartRaw) && mediaStartRaw > 0
                    ? mediaStartRaw
                    : 0;
                  const kindAttr = el.getAttribute("data-audio-kind");
                  const kind =
                    kindAttr === "music"
                      ? "music"
                      : kindAttr === "sfx"
                        ? "sfx"
                        : kindAttr === "voiceover"
                          ? "voiceover"
                          : undefined;
                  const fadeInRaw = parseFloat(el.getAttribute("data-fade-in") || "0");
                  const fadeOutRaw = parseFloat(el.getAttribute("data-fade-out") || "0");
                  const fadeInSec = Number.isFinite(fadeInRaw) && fadeInRaw > 0 ? fadeInRaw : 0;
                  const fadeOutSec = Number.isFinite(fadeOutRaw) && fadeOutRaw > 0 ? fadeOutRaw : 0;
                  const sBase = stateRef.current;
                  const kindMuted =
                    (kind === "sfx" && sBase.sfxMuted) ||
                    (kind === "music" && sBase.musicMuted) ||
                    (kind === "voiceover" && sBase.voiceoverMuted);
                  const initiallyMuted = sBase.muted || kindMuted;
                  el.muted = initiallyMuted;
                  el.volume = isFinite(vol) ? Math.max(0, Math.min(1, vol)) : 1;
                  el.preload = "auto";
                  // Initial-currentTime gleich auf den Schnittpunkt setzen,
                  // damit ein sofortiges Play (autoPlay) bereits an der
                  // richtigen Stelle einsetzt — sonst würde der Browser
                  // erst beim ersten tick() umspringen und der erste
                  // Frame klingt vom File-Start.
                  if (mediaStart > 0) {
                    try { el.currentTime = mediaStart; } catch { /* ignore */ }
                  }
                  collected.push({
                    el,
                    start: tStart,
                    duration: tDur,
                    baseVolume: el.volume,
                    mediaStart,
                    fadeInSec,
                    fadeOutSec,
                    kind,
                  });
                });
                stateRef.current.media = collected;
              }
              if (onReady) onReady(makeApi());
              if (restore?.wasPlaying || autoPlay) doPlay();
              // Frame-Listener einmal aufrufen, damit die TransportBar /
              // Timeline die wiederhergestellte Position sofort anzeigt.
              const cbs = stateRef.current.listeners.get("frameupdate");
              if (cbs && cbs.size > 0) {
                const frame = Math.round(initialT * stateRef.current.fps);
                cbs.forEach((cb) => cb({ detail: { frame } }));
              }
              return;
            }
          }
        }
        if (performance.now() - start < maxWaitMs) {
          setTimeout(poll, 50);
        } else {
          // eslint-disable-next-line no-console
          console.warn("[HfPlayer] timeline not resolved after " + maxWaitMs + "ms");
        }
      };
      setTimeout(poll, 50);
    };

    iframe.addEventListener("load", wire);
    return () => {
      iframe.removeEventListener("load", wire);
      const s = stateRef.current;
      if (s.raf) cancelAnimationFrame(s.raf);
      s.raf = 0;
      if (onReady) onReady(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remountKey]);

  // Helper: Mute-Status pro Element aus Master + Per-Kind Flag re-evaluieren.
  const reapplyMuteAll = (): void => {
    const s = stateRef.current;
    for (const m of s.media) {
      const kindMuted =
        (m.kind === "sfx" && s.sfxMuted) ||
        (m.kind === "music" && s.musicMuted) ||
        (m.kind === "voiceover" && s.voiceoverMuted);
      const shouldMute = s.muted || kindMuted;
      if (m.el.muted !== shouldMute) m.el.muted = shouldMute;
    }
  };

  useEffect(() => {
    stateRef.current.muted = initialMuted;
    reapplyMuteAll();
  }, [initialMuted]);

  // Per-Kind Mute-Toggles (sfx/music/voiceover) — Live, ohne Rebuild.
  useEffect(() => {
    stateRef.current.sfxMuted = sfxMuted;
    reapplyMuteAll();
  }, [sfxMuted]);
  useEffect(() => {
    stateRef.current.musicMuted = musicMuted;
    reapplyMuteAll();
  }, [musicMuted]);
  useEffect(() => {
    stateRef.current.voiceoverMuted = voiceoverMuted;
    reapplyMuteAll();
  }, [voiceoverMuted]);

  // Per-Kind Volume-Multiplier — werden in der nächsten `tick()`-Runde
  // angewandt. Wenn der Player gerade pausiert ist, wenden wir die
  // Volumes einmalig direkt auf jedes Element an, sodass ein folgendes
  // `play()` mit dem korrekten Lautstärkenpegel startet.
  useEffect(() => {
    stateRef.current.sfxVolume = sfxVolume;
    stateRef.current.musicVolume = musicVolume;
    stateRef.current.voiceoverVolume = voiceoverVolume;
    const s = stateRef.current;
    if (!s.paused) return;
    for (const m of s.media) {
      const kindVol =
        m.kind === "sfx" ? s.sfxVolume :
        m.kind === "music" ? s.musicVolume :
        m.kind === "voiceover" ? s.voiceoverVolume :
        1;
      const targetVol = Math.max(0, Math.min(1, m.baseVolume * kindVol));
      if (Math.abs(m.el.volume - targetVol) > 0.001) m.el.volume = targetVol;
    }
  }, [sfxVolume, musicVolume, voiceoverVolume]);

  // Keep the playback boundary in sync when the parent recomputes total
  // duration (e.g. user resized a scene on the timeline). Without this the
  // counter would stay locked to the value captured at iframe load and
  // diverge again from the transport-bar's `totalFrames` after edits.
  useEffect(() => {
    if (typeof durationInFrames !== "number" || durationInFrames <= 0) return;
    const s = stateRef.current;
    s.duration = durationInFrames / s.fps;
  }, [durationInFrames]);

  // VOR dem srcDoc-Reload die aktuelle Player-Zeit einfangen, damit `wire()`
  // nach dem Reload dorthin zurückseeken kann. useEffect feuert NACH dem
  // Render aber bevor das iframe das neue `srcDoc` geladen hat (load-Event
  // ist async) — der `wire`-Handler liest `restoreRef` dann aus.
  useEffect(() => {
    const s = stateRef.current;
    if (!s.tl) return;
    try {
      const now = performance.now();
      const t = s.paused
        ? s.playStartTime
        : s.playStartTime + ((now - s.playStartClock) / 1000) * s.rate;
      restoreRef.current = {
        time: Math.max(0, Math.min(t, s.duration || t)),
        wasPlaying: !s.paused,
      };
    } catch {
      /* ignore */
    }
  }, [srcDoc, src]);

  // ── DEBUG BRIDGE RECEIVER ───────────────────────────────────────────────
  // The composition HTML forwards console.* and uncaught errors from the
  // iframe via postMessage so the parent DevTools can see them without
  // switching frame context.
  useEffect(() => {
    function onMsg(ev: MessageEvent): void {
      const data = ev.data;
      if (!data || data.source !== "hf-iframe-log") return;
      const lvl = (data.level as string) || "log";
      const fn = (console as unknown as Record<string, (...a: unknown[]) => void>)[lvl] ?? console.log;
      fn("[hf-iframe]", data.message);
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Fullscreen-Listener: wenn der Wrapper fullscreen ist, merken wir uns
  // die echte Viewport-Größe und re-rendern, damit die Skalierung passt.
  // Auf `resize` reagieren wir auch, falls die Bildschirmrotation oder
  // -auflösung sich ändert, während wir im Vollbild sind.
  useEffect(() => {
    const measure = (): void => {
      const inFs = document.fullscreenElement === wrapperRef.current;
      if (inFs) {
        setFsSize({ w: window.innerWidth, h: window.innerHeight });
      } else {
        setFsSize(null);
      }
    };
    document.addEventListener("fullscreenchange", measure);
    window.addEventListener("resize", measure);
    return () => {
      document.removeEventListener("fullscreenchange", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);

  const containerW = fsSize?.w ?? displayWidth;
  const containerH = fsSize?.h ?? displayHeight;
  const scale = Math.min(containerW / compositionWidth, containerH / compositionHeight);
  const scaledW = compositionWidth * scale;
  const scaledH = compositionHeight * scale;
  const offsetX = Math.max(0, (containerW - scaledW) / 2);
  const offsetY = Math.max(0, (containerH - scaledH) / 2);

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        position: "relative",
        width: fsSize ? "100%" : displayWidth,
        height: fsSize ? "100%" : displayHeight,
        overflow: "hidden",
        background: "#06070b",
        ...style,
      }}
    >
      <iframe
        ref={iframeRef}
        title="hyperframes-preview"
        srcDoc={srcDoc}
        src={src}
        sandbox="allow-scripts allow-same-origin allow-popups"
        style={{
          position: "absolute",
          top: offsetY,
          left: offsetX,
          width: compositionWidth,
          height: compositionHeight,
          border: "0",
          transformOrigin: "0 0",
          transform: `scale(${scale})`,
          background: "transparent",
          pointerEvents: "none",
        }}
      />
    </div>
  );
});
